import React, { useEffect, useState, useCallback, useMemo } from 'react';

import {
  hasRoles,
  mergeRoles,
  VIEW_DATA_MANAGEMENT_DASHBOARD_ROLES,
  CREATE_STUDY_ROLES,
  VIEW_STUDY_ROLES,
  EDIT_STUDY_ROLES,
  CREATE_PARTICIPANT_REVIEW_ROLES,
  VIEW_PARTICIPANT_REVIEW_ROLES,
  EDIT_PARTICIPANT_REVIEW_ROLES,
} from '../../util/RoleUtil';

import { viewDataManagementDashboard, viewQualityControlDashboard } from '../../services/DashboardService';
import { retrieveNotifications } from '../../services/NotificationService';

import { DashboardButton } from '../common/button/dashboard-button/DashboardButton';
import { QualityControlDashboard } from './QualityControlDashboard';
import { DataCaptureDashboard } from './DataCaptureDashboard';
import { NotificationModal } from '../common/modal/notification-modal/NotificationModal';

const STUDY_ROLES = mergeRoles(CREATE_STUDY_ROLES, VIEW_STUDY_ROLES, EDIT_STUDY_ROLES);
const PARTICIPANT_REVIEW_ROLES = mergeRoles(
  CREATE_PARTICIPANT_REVIEW_ROLES,
  VIEW_PARTICIPANT_REVIEW_ROLES,
  EDIT_PARTICIPANT_REVIEW_ROLES
);

export const Dashboard = ({ context, setContext, navigateTo, showToaster, setSpinnerText }) => {
  const [showDataCaptureDashboard, setShowDataCaptureDashboard] = useState(false);
  const [showQualityControlDashboard, setShowQualityControlDashboard] = useState(true);
  const [dashboardQC, setDashboardQC] = useState({});
  const [dashboardDM, setDashboardDM] = useState({});
  const [activeStudies, setActiveStudies] = useState(0);
  const [openQueries, setOpenQueries] = useState(0);
  const [outstandingDataCaptures, setOutstandingDataCaptures] = useState(0);
  const [outstandingSDVCaptures, setOutstandingSDVCaptures] = useState(0);
  const [showNotifcationModal, setShowNotifcationModal] = useState(false);
  const [notifications, setNotifications] = useState([]);

  const retrieveQCDashboard = useCallback(async () => {
    setSpinnerText('Retrieving Quality Control Dashboard');
    const [data, error] = await viewQualityControlDashboard(context.username);
    if (data) {
      if (data.responseStatus === 'Success') {
        const vo = data.viewQualityControlDashboardVO;
        setDashboardQC(vo);
        setActiveStudies(vo.activeStudies);
        setOpenQueries(vo.openQueries);
        showToaster({
          responseStatus: 'Success',
          responseMessage: 'Quality Control Dashboard Successfully Retrieved.',
        });
      } else {
        showToaster(data);
      }
    } else {
      showToaster({
        responseStatus: 'Error',
        responseMessage: error.message,
      });
    }
    setSpinnerText(null);
  }, [context.username, setSpinnerText, showToaster]);

  const retrieveNotificationsForUser = useCallback(async () => {
    const [data, error] = await retrieveNotifications(context.username);
    if (data) {
      if (data.responseStatus === 'Success') {
        setNotifications(data.notifications);
      } else {
        showToaster(data);
      }
    } else {
      showToaster({
        responseStatus: 'Error',
        responseMessage: error.message,
      });
    }
  }, [context, showToaster]);

  useEffect(() => {
    retrieveQCDashboard();
  }, [retrieveQCDashboard]);

  useEffect(() => {
    retrieveNotificationsForUser();
    const interval = setInterval(() => {
      retrieveNotificationsForUser();
    }, 60 * 1000);

    return () => clearInterval(interval);
  }, [retrieveNotificationsForUser]);

  const canViewDataManagementDashboard = useMemo(
    () => hasRoles(context, VIEW_DATA_MANAGEMENT_DASHBOARD_ROLES),
    [context]
  );

  const retrieveDMDashboard = async () => {
    setSpinnerText('Retrieving Data Management Dashboard');
    const [data, error] = await viewDataManagementDashboard(context.username);
    if (data) {
      if (data.responseStatus === 'Success') {
        const vo = data.viewDataManagementDashboardVO;
        setDashboardDM(vo);
        setOutstandingDataCaptures(vo.outstandingDataCaptures);
        setOutstandingSDVCaptures(vo.outstandingSDVCaptures);
        showToaster({
          responseStatus: 'Success',
          responseMessage: 'Data Management Dashboard Successfully Retrieved.',
        });
      } else {
        showToaster(data);
      }
    } else {
      showToaster({
        responseStatus: 'Error',
        responseMessage: error.message,
      });
    }
    setSpinnerText(null);
  };

  const switchDashboard = async () => {
    if (showQualityControlDashboard) {
      await retrieveDMDashboard();
      setShowDataCaptureDashboard(true);
      setShowQualityControlDashboard(false);
    } else if (setShowDataCaptureDashboard) {
      await retrieveQCDashboard();
      setShowDataCaptureDashboard(false);
      setShowQualityControlDashboard(true);
    }
  };

  const handleActiveStudiesClick = () => {
    navigateTo('studyTrialAdmin');
  };

  const handleOpenQueriesClick = () => {
    navigateTo('participantReviewsTable');
  };

  const renderDuration = (duration) => {
    if (!duration) return '-';

    const splitDuration = duration.split(' ');

    const firstDate = splitDuration[0];
    const secondDate = splitDuration[3];

    if (firstDate && secondDate) return `${firstDate} - ${secondDate}`;

    return firstDate;
  };

  return (
    <div className="tab-container justify-initial">
      <label className="dashboard-header">
        {showQualityControlDashboard ? 'Quality Control Dashboard' : 'Data Management Dashboard'}
      </label>
      <div className="form-section">
        <div className="button-container-dashboard">
          {showQualityControlDashboard ? (
            <>
              <DashboardButton
                title="Active Studies: "
                width={canViewDataManagementDashboard ? '30%' : '33.3%'}
                onClick={handleActiveStudiesClick}
                text={activeStudies}
                disabled={!hasRoles(context, STUDY_ROLES)}
                hoverable
              />
              <DashboardButton
                title="Open Queries: "
                width={canViewDataManagementDashboard ? '30%' : '33.3%'}
                onClick={handleOpenQueriesClick}
                text={openQueries}
                disabled={!hasRoles(context, PARTICIPANT_REVIEW_ROLES)}
                hoverable
              />
            </>
          ) : (
            <>
              <DashboardButton
                title="Outstanding Data Captures: "
                width={canViewDataManagementDashboard ? '30%' : '33.3%'}
                text={outstandingDataCaptures}
                hoverable
              />
              <DashboardButton
                title="Outstanding SDV Captures: "
                width={canViewDataManagementDashboard ? '30%' : '33.3%'}
                text={outstandingSDVCaptures}
                hoverable
              />
            </>
          )}
          <DashboardButton
            title="Notifications: "
            width={canViewDataManagementDashboard ? '30%' : '33.4%'}
            onClick={() => setShowNotifcationModal(true)}
            text={notifications.filter((notification) => !notification.acknowledged).length}
            hoverable
          />
          {canViewDataManagementDashboard && (
            <DashboardButton title="Switch Dashboard" width="10%" onClick={switchDashboard} hoverable />
          )}
        </div>
      </div>

      {showQualityControlDashboard && (
        <QualityControlDashboard dashboard={dashboardQC} renderDuration={renderDuration} />
      )}
      {showDataCaptureDashboard && <DataCaptureDashboard dashboard={dashboardDM} renderDuration={renderDuration} />}

      {showNotifcationModal && (
        <NotificationModal
          notifications={notifications}
          onClose={() => setShowNotifcationModal(false)}
          setSpinnerText={setSpinnerText}
          showToaster={showToaster}
          context={context}
          onAcknowledgeSuccess={() => retrieveNotificationsForUser()}
          setContext={setContext}
        />
      )}
    </div>
  );
};
